import React, { useContext, useEffect, useState } from 'react';
import {
  PdfBarcodeComponent,
  PdfComponent,
  PdfFieldComponent,
  PdfImageComponent,
  PdfOrientation,
  PdfPageFormat,
  PdfRepeat,
  PdfStaticTextComponent,
  PdfTableType,
  PdfTemplateDocumentProperties,
  PdfTemplateProperties,
} from './PdfComponent';
import PdfTemplateEditorCanvas from './canvas/PdfTemplateEditorCanvas';
import {
  PdfTemplateEditorContext,
  PdfTemplateEditorSettings,
  PdfTableDataProvider,
  PdfTemplateEditorContextValue,
  PdfEditorPreviewPage,
} from './PdfTemplateEditorContext';
import { PdfComponentFontProperties } from './PdfComponent';
import PdfTemplateEditorPropertiesBar from './properties/PdfTemplateEditorPropertiesBar';
import PdfTemplateEditorSidebar from './sidebar/PdfTemplateEditorSidebar';
import PdfTemplateEditorToolbar from './toolbar/PdfTemplateEditorToolbar';
import { IconButton } from '../../icons';
import { IconType } from '../../icons/Icon';
import { pdfFontFamilyToCss, PdfTemplateUtil } from './pdfTemplate.util';
import { PdfTableComponent } from './PdfComponent';
import { PdfDataKey } from './data/PdfDataKey';
import { OrderContext } from '../../../context/OrderContext';
import { PdfFieldResolving } from './data/PdfFieldResolving';
import { ProductMasterDataContext } from '../../../context/ProductMasterDataContext';
import { ProductTransactionContext } from '../../../context/ProductTransactionContext';
import { CustomFieldContext } from '../../../context/CustomFieldContext';
import { BinContext } from '../../../context/BinContext';
import { LotContext } from '../../../context/LotContext';
import { StockLocationContext } from '../../../context/StockLocationContext';
import { ContainerContext } from '../../../context/ContainerContext';
import { cloneDeep } from '@apollo/client/utilities';
import { BarcodeExportConfigurationEntry } from '../../../types/barcode_export_configuration';

function usePdfTemplateData(): PdfFieldResolving.Data {
  const { orders } = useContext(OrderContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { productTransactions } = useContext(ProductTransactionContext);
  const { customFields } = useContext(CustomFieldContext);
  const { bins } = useContext(BinContext);
  const { lots } = useContext(LotContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { containers } = useContext(ContainerContext);

  return {
    orders,
    productMasterData,
    productTransactions,
    customFields,
    bins,
    lots,
    stockLocations,
    containers,
    pdf: { page: 1, totalPages: 1 },
  };
}

export interface PdfTemplatePropertiesEditorDefaultComponents {
  staticText: () => PdfStaticTextComponent;
  field: () => PdfFieldComponent;
  table?: (tableType: PdfTableType) => PdfTableComponent;
  image: () => PdfImageComponent;
  barcode: () => PdfBarcodeComponent;
}

export interface PdfTemplatePropertiesEditorSettings {
  pageFormats: PdfPageFormat[];
  orientations: PdfOrientation[];
  repeatModes: PdfRepeat[];
  defaultGridSizeMm: number;
  defaultZoom: number;
  barcodeExportEntries: BarcodeExportConfigurationEntry[];
}

export interface PdfPreviewProperties {
  handleSample: (properties: PdfTemplateProperties) => void;
  preview: React.ReactNode;
}

interface PdfTemplatePropertiesEditorProps<T> {
  dataKeys: PdfDataKey<any>[];
  imageDataKeys: PdfDataKey<any>[];
  tableProviders: PdfTableDataProvider<T>[];
  properties: PdfTemplateProperties;
  previewProperties?: PdfPreviewProperties;
  onDone: (properties: PdfTemplateProperties) => void;
  settings: PdfTemplatePropertiesEditorSettings;
  defaultComponents: PdfTemplatePropertiesEditorDefaultComponents;
}

export default function PdfTemplatePropertiesEditor<T>({
  dataKeys,
  imageDataKeys,
  tableProviders,
  properties,
  previewProperties,
  onDone,
  settings,
  defaultComponents,
}: PdfTemplatePropertiesEditorProps<T>) {
  const data = usePdfTemplateData();

  const [input, setInput] = useState<PdfTemplateProperties>(properties);

  useEffect(() => {
    setInput(properties);
  }, [properties]);

  const documentProperties = input.document;
  const components = input.components;

  const updateDocumentProperties = (updates: Partial<PdfTemplateDocumentProperties>) => {
    setInput(i => {
      i.document = { ...i.document, ...updates };
      return cloneDeep(i);
    });
  };

  const setComponents = (components: PdfComponent[]) => {
    setInput(i => {
      i.components = components;
      return cloneDeep(i);
    });
  };
  const updateComponent = (id: string, updates: Partial<PdfComponent>) => {
    setComponents(components.map(c => (c.id === id ? ({ ...c, ...updates } as PdfComponent) : c)));
  };

  const [editorSettings, setEditorSettings] = useState<PdfTemplateEditorSettings>({
    gridMode: true,
    propertiesBarWidth: 330,
    sidebarWidth: 330,
    zoom: settings.defaultZoom,
    sidebarOpen: true,
    gridMm: settings.defaultGridSizeMm,
    previewPage: PdfEditorPreviewPage.first,
  });
  const updateEditorSettings = (updates: Partial<PdfTemplateEditorSettings>) => {
    setEditorSettings(prev => ({ ...prev, ...updates }));
  };

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = components ? components.find(c => c.id === selectedId) : undefined;
  const [selectedEntity, setSelectedEntity] = useState<any | null>(null);

  const deleteComponent = (id: string) => {
    setComponents(components.filter(c => c.id !== id));
  };

  const context: PdfTemplateEditorContextValue<T> = {
    input,
    data,
    settings,
    previewProperties,
    defaultComponents,
    documentProperties,
    updateDocumentProperties,
    components,
    setComponents,
    updateComponent,
    deleteComponent,
    selectedId,
    setSelectedId,
    selected,
    editorSettings,
    setEditorSettings,
    updateEditorSettings,
    dataKeys,
    imageDataKeys,
    tableProviders,
    selectedEntity,
    setSelectedEntity,
    fontToCss: (font: PdfComponentFontProperties) => ({
      fontSize: `${font.size * editorSettings.zoom}pt`,
      color: font.color,
      ...pdfFontFamilyToCss(font.family),
    }),
    // Zoom-aware helpers
    mmToPxScaled: (mm: number) => PdfTemplateUtil.mmToPx(mm) * editorSettings.zoom,
    pxToMmScaled: (px: number) => PdfTemplateUtil.pxToMm(px / editorSettings.zoom),
    fsToPtScaled: (pt?: number) => (pt ?? documentProperties.font.size) * editorSettings.zoom,
    gridSizePx: () => PdfTemplateUtil.mmToPx(editorSettings.gridMm) * editorSettings.zoom,
    snapMm: (mm: number) => {
      const g = editorSettings.gridMm;
      const snapped = Math.round(mm / g) * g;
      return snapped;
    },
    tableColumnWidthsPx: (component: PdfTableComponent) => {
      return component.columns.map(col => PdfTemplateUtil.mmToPx(col.width) * editorSettings.zoom);
    },
    componentBoxPx: (comp: { x: number; y: number; width: number; height: number }) => ({
      x: PdfTemplateUtil.mmToPx(comp.x) * editorSettings.zoom,
      y: PdfTemplateUtil.mmToPx(comp.y) * editorSettings.zoom,
      width: PdfTemplateUtil.mmToPx(comp.width) * editorSettings.zoom,
      height: PdfTemplateUtil.mmToPx(comp.height) * editorSettings.zoom,
    }),
    eventPointPxToMm: (clientX: number, clientY: number, canvasOffset: { left: number; top: number }) => ({
      xMm: PdfTemplateUtil.pxToMm((clientX - canvasOffset.left) / editorSettings.zoom),
      yMm: PdfTemplateUtil.pxToMm((clientY - canvasOffset.top) / editorSettings.zoom),
    }),
  };

  return (
    <PdfTemplateEditorContext.Provider value={context}>
      <div className='relative z-10 flex h-full w-full justify-between overflow-hidden'>
        <div
          className='bg-gray-50'
          style={{
            visibility: editorSettings.sidebarOpen ? 'visible' : 'hidden',
            width: editorSettings.sidebarOpen ? editorSettings.sidebarWidth : 0,
          }}
        >
          <div className='relative h-full w-full border-r border-gray-300'>
            <PdfTemplateEditorSidebar onSave={() => onDone(input)} />
          </div>
        </div>

        {!editorSettings.sidebarOpen && (
          <div className='pointer-events-auto fixed left-2 top-1/2 z-10 -translate-y-1/2'>
            <IconButton
              type={editorSettings.sidebarOpen ? IconType.chevronLeft : IconType.chevronRight}
              onClick={() => updateEditorSettings({ sidebarOpen: !editorSettings.sidebarOpen })}
            />
          </div>
        )}

        <div className='absolute inset-0 z-[-1]'>
          <PdfTemplateEditorCanvas />
          <PdfTemplateEditorToolbar />
        </div>

        {selected && (
          <div
            style={{ width: editorSettings.propertiesBarWidth }}
            className='h-full overflow-x-hidden border-l border-gray-300 bg-gray-50'
          >
            <PdfTemplateEditorPropertiesBar />
          </div>
        )}
      </div>
    </PdfTemplateEditorContext.Provider>
  );
}
